import { gql, useMutation } from '@apollo/client';
import { apm } from '@elastic/apm-rum';
import dayjs from 'dayjs';
import { merge } from 'lodash-es';
import { useSelector } from 'react-redux';

import { updateUser } from '../../../actions/entities';
import { User } from '../../../models/User';
import { getUser } from '../../../selectors/user';
import { useAppDispatch } from '../../../store';

const mutation = gql`
  mutation confirmWhatsAppNumber($input: UpdateMeInput!) {
    confirmWhatsAppNumber: updateMe(me: $input) {
      id
      updatedAt
      metadata
    }
  }
`;

type QueryInput = {
  input: Record<string, unknown>;
};

type QueryOutput = {
  confirmWhatsAppNumber: Pick<User, 'updatedAt' | 'id' | 'metadata'>;
};

type Props = {
  onSuccess?: (data: QueryOutput['confirmWhatsAppNumber']) => void;
};

export default function useConfirmWhatsAppNumber({ onSuccess }: Props = {}) {
  const dispatch = useAppDispatch();
  const user: User = useSelector(getUser);
  const [updateUserMutation] = useMutation<QueryOutput, QueryInput>(mutation);

  function updateWAConfirmationTimestamp(
    metadataToUpdate: Record<string, unknown>
  ) {
    const mergedMetadata = merge(
      { ...(user.metadata as Record<string, unknown>) },
      metadataToUpdate
    ) as Record<string, unknown>;
    dispatch(
      updateUser(user.id, {
        updatedAt: dayjs().toISOString(),
        metadata: {
          ...mergedMetadata,
        },
      })
    );
  }

  const refreshUpdatedAt = async () => {
    const metadataToUpdate = {
      lastWhatsappNumberConfirmedAt: dayjs().utc().toISOString(),
    };
    updateWAConfirmationTimestamp(metadataToUpdate);
    await updateUserMutation({
      variables: {
        input: {
          metadata: metadataToUpdate,
        },
      },
      onCompleted: data => {
        onSuccess?.(data.confirmWhatsAppNumber);
        updateWAConfirmationTimestamp(
          data.confirmWhatsAppNumber.metadata as Record<string, unknown>
        );
      },
      onError: error => {
        apm.captureError(error);
      },
    });
  };

  return {
    refreshUpdatedAt,
  } as const;
}
